import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Ban, TriangleAlert } from 'lucide-react';
import {
  seatPoolsForRoles,
  SIGN_IN_ELIGIBLE_USER_STATUSES,
  type OrgMemberListItem,
  type SeatPool,
  type UserStatus
} from '@bliks-io/contracts';
import {
  Badge,
  Button,
  DataTable,
  DataTableSkeleton,
  DeleteConfirmDialog,
  Skeleton,
  createActionsColumn,
  createColumnHeader,
  createTextColumn,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { useOrgMembers } from '@/api/queries/useOrgMembers';
import { useOrganizationSeatUsage } from '@/api/queries/useSeatUsage';
import { useDeleteOrgMember } from '@/api/mutations/useOrgMembers';
import type { ApiError } from '@/lib/agent.types';
import { translateErrorMessage } from '@/lib/errors';
import { toastRequestError } from '@/api/mutations/toast-api-error';
import { SEAT_POOLS } from '@/lib/seat-limits';
import { getUserStatusColorScheme, getUserStatusLabel } from '@/lib/user-utils';
import { useOrgMemberStatusActions } from '@/pages/organization/org-members/useOrgMemberStatusActions';
import { SeatOverrunMessage } from './SeatOverrunMessage';

interface SeatOverrunResolverProps {
  currentUserId: string;
}

/**
 * The repair surface an org-manager or system-admin gets on the lock screen:
 * the over-full pools and the seats they hold, with the only two writes the
 * backend still allows during an overrun (suspend, delete).
 */
export function SeatOverrunResolver({ currentUserId }: SeatOverrunResolverProps) {
  const { t } = useTranslation();
  // 'always': the lock page must never repair against a cached under-cap
  // reading left behind by SeatUsageSummary before the cap was lowered.
  const {
    data: usage,
    isPending: usageLoading,
    isFetching: usageFetching,
    isError: usageFailed,
    refetch: refetchUsage
  } = useOrganizationSeatUsage({ refetchOnMount: 'always' });
  // Deliberately unfiltered beyond the seat-holding statuses: external members
  // hold seats too, and every pool is in scope. The status set is the one the
  // backend's pool filters count, so a seat that causes the overrun cannot be
  // missing from the table that repairs it.
  const {
    data: orgMembers,
    isLoading,
    isFetching: membersFetching,
    isError: membersFailed,
    refetch: refetchMembers
  } = useOrgMembers({ status: [...SIGN_IN_ELIGIBLE_USER_STATUSES] }, { refetchOnMount: 'always' });
  const { handleSuspend, pendingMemberIds } = useOrgMemberStatusActions();
  const deleteMutation = useDeleteOrgMember();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { overFullPools, rows } = useMemo(() => {
    const overFull = usage
      ? SEAT_POOLS.flatMap((descriptor) => {
          const { limit, used } = usage[descriptor.usageKey];
          return limit !== null && used > limit ? [{ ...descriptor, over: used - limit }] : [];
        })
      : [];
    const poolIds = new Set<SeatPool>(overFull.map(({ pool }) => pool));
    return {
      overFullPools: overFull,
      rows: (orgMembers ?? []).filter((member) =>
        seatPoolsForRoles(member.userTypes).some((pool) => poolIds.has(pool))
      )
    };
  }, [orgMembers, usage]);

  // Every repair acts on the "over by N" and the rows shown: none may start
  // while either is being re-read or another repair is still moving them.
  const repairBusy =
    pendingMemberIds.size > 0 || deleteMutation.isPending || usageFetching || membersFetching;
  const deletingListed = rows.some((member) => member._id === deletingId);
  // Dropped during render, so a member listed again later does not reopen the confirm.
  if (deletingId !== null && !deletingListed) setDeletingId(null);

  const columns = useMemo<DataTableColumnDef<OrgMemberListItem>[]>(
    () => [
      {
        id: 'name',
        accessorFn: (row) => `${row.firstName} ${row.lastName}`,
        meta: { label: t('common.Name') },
        header: createColumnHeader(t('common.Name'))
      },
      createTextColumn<OrgMemberListItem>('email', t('common.Email')),
      {
        id: 'seatPool',
        meta: { label: t('organization.seatOverrun.seatPool') },
        header: createColumnHeader(t('organization.seatOverrun.seatPool')),
        // Locale-dependent, so it lives in `cell`: TanStack caches accessor
        // values per row and would freeze the labels of the first locale.
        cell: ({ row }) => {
          const pools = seatPoolsForRoles(row.original.userTypes);
          return SEAT_POOLS.filter(({ pool }) => pools.includes(pool))
            .map(({ labelKey }) => t(labelKey))
            .join(', ');
        },
        enableSorting: false
      },
      {
        accessorKey: 'status',
        meta: { label: t('common.Status') },
        header: createColumnHeader(t('common.Status')),
        cell: ({ row }) => {
          const status = row.getValue('status') as UserStatus;
          return (
            <Badge colorScheme={getUserStatusColorScheme(status)}>
              {getUserStatusLabel(status, t)}
            </Badge>
          );
        }
      },
      createActionsColumn<OrgMemberListItem>({
        onDelete: (member) => setDeletingId(member._id),
        // Not getMemberStatusActions: it also offers resend-invitation, which
        // the seat lock refuses.
        getCustomActions: (member) => [
          {
            label: t('organization.members.suspendUser'),
            icon: <Ban className="w-4 h-4" />,
            variant: 'destructive',
            onClick: () => void handleSuspend(member._id)
          }
        ],
        isRowPending: (member) => pendingMemberIds.has(member._id),
        // The API allows self-suspend/-delete for these roles (a superadmin can
        // re-grant them), but here it would kill the resolver's own session
        // mid-repair and push recovery out to a superadmin.
        isRowDisabled: (member) => member._id === currentUserId || repairBusy
      })
    ],
    [t, currentUserId, handleSuspend, pendingMemberIds, repairBusy]
  );

  if (isLoading || usageLoading) {
    return (
      <div className="w-full max-w-5xl" role="status" aria-label={t('common.loading')}>
        <Skeleton className="h-8 w-80" />
        <Skeleton className="mt-3 h-5 w-full" />
        <Skeleton className="mt-4 h-5 w-64" />
        <DataTableSkeleton columnCount={columns.length} className="mt-8" />
      </div>
    );
  }

  // A failed read settles with no data, which would otherwise look exactly like
  // "the over-full pools hold nobody" — a dead end for the only person who can
  // lift the lock.
  if (usageFailed || membersFailed) {
    return (
      <div className="max-w-md self-center text-center">
        <TriangleAlert className="mx-auto h-12 w-12 text-destructive" />
        <p className="mt-6 text-sm text-muted-foreground">
          {t('organization.seatOverrun.loadError')}
        </p>
        <Button
          className="mt-4"
          variant="outline"
          loading={usageFetching || membersFetching}
          onClick={() => {
            void refetchUsage();
            void refetchMembers();
          }}
        >
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  if (!rows.some((member) => member._id !== currentUserId)) {
    return <SeatOverrunMessage description={t('organization.seatOverrun.stuckDescription')} />;
  }

  return (
    <div className="w-full max-w-5xl">
      <h1 className="text-2xl font-semibold text-foreground">
        {t('organization.seatOverrun.title')}
      </h1>
      <p className="mt-3 text-sm text-muted-foreground">
        {t('organization.seatOverrun.resolverDescription')}
      </p>
      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
        {overFullPools.map(({ usageKey, labelKey, over }) => (
          <span key={usageKey}>
            {t(labelKey)}: {t('organization.seatOverrun.overBy', { over })}
          </span>
        ))}
      </div>
      <div className="mt-8">
        <DataTable columns={columns} data={rows} />
      </div>
      <DeleteConfirmDialog
        open={deletingListed}
        onOpenChange={(open) => !open && setDeletingId(null)}
        isDeleting={repairBusy}
        entityPhrase={t('organization.members.deletePhrase')}
        onConfirm={async () => {
          if (!deletingId) return;
          try {
            await deleteMutation.mutateAsync(deletingId);
            setDeletingId(null);
          } catch (error) {
            toastRequestError(error, translateErrorMessage(error as ApiError, t));
          }
        }}
      />
    </div>
  );
}
